import { App, Button, Flex, Popconfirm, Typography, Upload } from 'antd';
import { CameraOutlined, DeleteOutlined } from '@ant-design/icons';
import { translateApiError } from '@/shared/api/errorToMessage';
import { useBreakpoint } from '@/shared/lib/hooks';
import { IMAGE_ACCEPT, validateImageFile } from '@/shared/lib/image-file';
import { ImageThumb } from '@/shared/ui/ImageThumb';
import { useRemoveCategoryImageMutation, useUploadCategoryImageMutation } from '../api/categoryApi';
import type { CategoryDto } from '../model/types';
import './CategoryImageField.mobile.css';

export interface CategoryImageFieldProps {
  category: Pick<CategoryDto, 'id' | 'name' | 'imageUrl' | 'thumbUrl'>;
}

/** §2.1: фото существующей группы или категории — просмотр, замена, удаление. В отличие от полей
 *  окна, фото сохраняется сразу, без кнопки «Сохранить»: это отдельный запрос с файлом. */
export function CategoryImageField({ category }: CategoryImageFieldProps) {
  const { message } = App.useApp();
  const { isMobile } = useBreakpoint();
  const [uploadImage, { isLoading: uploading }] = useUploadCategoryImageMutation();
  const [removeImage, { isLoading: removing }] = useRemoveCategoryImageMutation();
  const busy = uploading || removing;
  const hasPhoto = !!category.imageUrl;

  function beforeUpload(file: File): false {
    const reason = validateImageFile(file);
    if (reason) {
      message.warning(reason);
      return false;
    }
    uploadImage({ id: category.id, file })
      .unwrap()
      .then(() => message.success('Фото сохранено'))
      .catch((e: unknown) => message.error(translateApiError(e)));
    return false; // отправили сами через RTK Query; antd Upload ничего не шлёт
  }

  async function handleRemove() {
    try {
      await removeImage(category.id).unwrap();
      message.success('Фото удалено');
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  // Телефон: фото крупно сверху, под ним — кнопки на всю ширину (≥ 44px). Без фото вместо кнопки —
  // крупная область «Загрузить фото»: касание открывает системный выбор «Снять фото / Медиатека / Файлы».
  if (isMobile) {
    return (
      <Flex vertical gap={12} className="category-image-field--mobile">
        {hasPhoto ? (
          <Flex justify="center">
            <ImageThumb src={category.thumbUrl} previewSrc={category.imageUrl} size={192} alt={category.name} />
          </Flex>
        ) : null}
        {/* fileList пустой всегда: antd Upload не копит выбранные файлы у себя */}
        <Upload accept={IMAGE_ACCEPT} showUploadList={false} fileList={[]} beforeUpload={beforeUpload} disabled={busy}>
          {hasPhoto ? (
            <Button block size="large" icon={<CameraOutlined />} loading={uploading} disabled={removing}>
              Заменить фото
            </Button>
          ) : (
            <Button
              block
              type="dashed"
              size="large"
              icon={<CameraOutlined style={{ fontSize: 28 }} />}
              loading={uploading}
              disabled={removing}
              style={{ height: 120, flexDirection: 'column', gap: 4 }}
            >
              Загрузить фото
            </Button>
          )}
        </Upload>
        {hasPhoto ? (
          <Popconfirm title="Удалить фото?" okText="Удалить" cancelText="Отмена" onConfirm={() => void handleRemove()}>
            <Button block danger size="large" icon={<DeleteOutlined />} loading={removing} disabled={uploading}>
              Удалить фото
            </Button>
          </Popconfirm>
        ) : null}
        <Typography.Text type="secondary">JPG, PNG или WebP, до 10 МБ. Фото сохраняется сразу.</Typography.Text>
      </Flex>
    );
  }

  return (
    <Flex gap={12} align="flex-start">
      <ImageThumb src={category.thumbUrl} previewSrc={category.imageUrl} size={96} alt={category.name} />
      <Flex vertical gap={6} align="flex-start">
        {/* fileList пустой всегда: antd Upload не копит выбранные файлы у себя */}
        <Upload accept={IMAGE_ACCEPT} showUploadList={false} fileList={[]} beforeUpload={beforeUpload} disabled={busy}>
          <Button loading={uploading} disabled={removing}>
            {hasPhoto ? 'Заменить фото' : 'Загрузить фото'}
          </Button>
        </Upload>
        {hasPhoto ? (
          <Popconfirm title="Удалить фото?" okText="Удалить" cancelText="Отмена" onConfirm={() => void handleRemove()}>
            <Button danger loading={removing} disabled={uploading}>
              Удалить фото
            </Button>
          </Popconfirm>
        ) : null}
        <Typography.Text type="secondary">JPG, PNG или WebP, до 10 МБ. Фото сохраняется сразу.</Typography.Text>
      </Flex>
    </Flex>
  );
}
